import { useEffect, useRef } from 'react';

import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
  QueryKey,
} from '@tanstack/react-query';

export type QueryWithCallbacksOptions<
  TData = unknown,
  TError = Error,
  TQueryKey extends QueryKey = QueryKey
> = {
  queryKey: TQueryKey;
  queryFn: () => Promise<TData>;
  onSuccess?: (data: TData) => void;
  onError?: (error: TError) => void;
} & Omit<
  UseQueryOptions<TData, TError, TData, TQueryKey>,
  'queryKey' | 'queryFn'
>;

/**
 * This is a custom hook that supports callback functions for success and error states in TanStack Query v5.
 * It provides a way to handle these callbacks using useEffect, as the onSuccess and onError callbacks were removed in v5.
 */
export default function useQueryWithCallbacks<
  TData = unknown,
  TError = Error,
  TQueryKey extends QueryKey = QueryKey
>({
  queryKey,
  queryFn,
  onSuccess,
  onError,
  ...queryOptions
}: QueryWithCallbacksOptions<TData, TError, TQueryKey>): UseQueryResult<
  TData,
  TError
> {
  const query = useQuery({
    queryKey,
    queryFn,
    ...queryOptions,
  });

  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
  }, [onSuccess, onError]);

  useEffect(() => {
    if (query.data && onSuccessRef.current) {
      onSuccessRef.current(query.data);
    }
  }, [query.data]);

  useEffect(() => {
    if (query.error && onErrorRef.current) {
      onErrorRef.current(query.error);
    }
  }, [query.error]);

  return query;
}
